// Folds the sphinx run event stream (`SphinxEvent`, GET /runs/:id/stream) into
// per-iteration live state for the run detail matrix + drawer. Polling
// (`useSphinxRun`) stays the source of truth; this only fills the gap between
// polls (streamed text, tool calls, early status) and self-heals on the next
// poll if an event is missed.
import type {
  Grade,
  IterationStatus,
  SphinxEvent,
  SphinxModel,
  TargetId,
} from "@/shared/sphinx";

export const SPHINX_EVENT_TYPES: readonly SphinxEvent["type"][] = [
  "run_start",
  "queue_position",
  "iteration_start",
  "text_delta",
  "tool_call",
  "tool_result",
  "iteration_finish",
  "grade",
  "error",
  "complete",
];

export type LiveToolCall = {
  name: string;
  args?: unknown;
  resultSummary?: string;
  isError?: boolean;
  /** From Yarn's own logs (queue/live-tail.ts), not the client's surface. */
  serverRecorded?: boolean;
  /** Which connector served the call — multi-connector runs call the same tool once per org. */
  server?: string;
};

export type LiveIteration = {
  iterationId: string;
  target?: TargetId;
  model?: SphinxModel;
  /** running after iteration_start; the terminal status after iteration_finish. */
  status?: IterationStatus;
  queuePosition?: number;
  text: string;
  toolCalls: LiveToolCall[];
  grade?: Grade;
  error?: string;
};

export type SphinxRunStreamState = {
  /** Highest SSE row id applied; reconnects resume with `?after=`. */
  lastEventId: number;
  started: boolean;
  complete: boolean;
  summary?: { total: number; passed: number; failed: number };
  /** Run-level error (an `error` event without an iterationId). */
  runError?: string;
  iterations: Record<string, LiveIteration>;
};

export const initialSphinxRunStreamState: SphinxRunStreamState = {
  lastEventId: 0,
  started: false,
  complete: false,
  iterations: {},
};

const emptyIteration = (iterationId: string): LiveIteration => ({
  iterationId,
  text: "",
  toolCalls: [],
});

function withIteration(
  state: SphinxRunStreamState,
  iterationId: string,
  patch: (it: LiveIteration) => LiveIteration
): SphinxRunStreamState {
  const current = state.iterations[iterationId] ?? emptyIteration(iterationId);
  return {
    ...state,
    iterations: { ...state.iterations, [iterationId]: patch(current) },
  };
}

/** Parse one SSE `data:` payload; null when it is not a SphinxEvent. */
export function parseSphinxEvent(data: string): SphinxEvent | null {
  try {
    const parsed = JSON.parse(data) as { type?: unknown };
    return parsed && typeof parsed.type === "string" ? (parsed as SphinxEvent) : null;
  } catch {
    return null;
  }
}

export function reduceSphinxEvent(
  state: SphinxRunStreamState,
  event: SphinxEvent,
  eventId = state.lastEventId
): SphinxRunStreamState {
  const base =
    eventId > state.lastEventId ? { ...state, lastEventId: eventId } : state;

  switch (event.type) {
    case "run_start":
      return { ...base, started: true };

    case "queue_position":
      return withIteration(base, event.iterationId, (it) => ({
        ...it,
        queuePosition: event.position,
      }));

    case "iteration_start":
      return withIteration(base, event.iterationId, (it) => ({
        ...it,
        target: event.target,
        model: event.model,
        status: "running",
        queuePosition: undefined,
      }));

    case "text_delta":
      return withIteration(base, event.iterationId, (it) => ({
        ...it,
        text: it.text + event.text,
      }));

    case "tool_call":
      return withIteration(base, event.iterationId, (it) => ({
        ...it,
        toolCalls: [
          ...it.toolCalls,
          {
            name: event.name,
            args: event.args,
            ...(event.serverRecorded ? { serverRecorded: true } : {}),
            ...(typeof (event as { server?: unknown }).server === "string"
              ? { server: (event as { server: string }).server }
              : {}),
          },
        ],
      }));

    case "tool_result":
      return withIteration(base, event.iterationId, (it) => {
        // Results arrive in call order: attach to the oldest call of that
        // name still awaiting one.
        const idx = it.toolCalls.findIndex(
          (c) => c.name === event.name && c.resultSummary === undefined
        );
        const result = { resultSummary: event.summary, isError: event.isError };
        const toolCalls =
          idx === -1
            ? [...it.toolCalls, { name: event.name, ...result }]
            : it.toolCalls.map((c, i) => (i === idx ? { ...c, ...result } : c));
        return { ...it, toolCalls };
      });

    case "iteration_finish":
      return withIteration(base, event.iterationId, (it) => ({
        ...it,
        status: event.status,
      }));

    case "grade":
      return withIteration(base, event.iterationId, (it) => ({
        ...it,
        grade: event.grade,
      }));

    case "error":
      if (!event.iterationId) return { ...base, runError: event.message };
      return withIteration(base, event.iterationId, (it) => ({
        ...it,
        error: event.message,
      }));

    case "complete":
      return { ...base, complete: true, summary: event.summary };

    default:
      return base;
  }
}
